/** @jsxRuntime classic */
/** @jsx jsx */
import {css, jsx} from "@emotion/core";
import React from 'react';
import {getApi, getAppState} from "../store/AppState";
import {getAppComponent, getMapComponent} from "../store/Accessor";
import {PLATTS_0_60, PLATTS_40_100, POLYGON_DATASET_1, VESSELS} from "../__tests__/TestDatasets";
import {Color} from "../util/Color";
import {LayerUtil} from "../map/LayerUtil";
import {Button, Container, Dialog, Table, TextArea, TextInput, Toolbar, ToolbarSpacer} from "@ice/components";
import {Dataset} from "../data/Dataset";
import {LayerDef} from "../types";
import {PolygonProvider} from "../util/PolygonProvider";

interface DevAction {
    name: string;
    action?: () => void;
}


interface DevOptionsPanelProps {
}


interface DevOptionsPanelState {
}


export default class DevOptionsPanel extends React.Component<DevOptionsPanelProps, DevOptionsPanelState> {

    private readonly ACTIONS: DevAction[] = [
        {name: "Sample datasets"},
        {name: "Add Platts Data (0% - 60%)", action: () => this.addPlatts0_60()},
        {name: "Add Platts Data (40% - 100%)", action: () => this.addPlatts40_100()},
        {name: "Add Vessel Data", action: () => this.addVesselData()},
        {name: "Add Polygon Data", action: () => this.addPolygonData1()},
        {name: "App Internal"},
        {name: "SET / GET state", action: () => this.manageState()},
        {name: "Delete all datasets", action: () => this.deleteAllData()},
        {name: "Polygon Service"},
        {name: "Test Polygon Service", action: () => this.testPolygonService()},
    ];

    constructor(props: DevOptionsPanelProps) {
        super(props);
        this.state = {}
    };

    private addDatasetAndLayer = (dataset: Dataset, layerDef: LayerDef) => {
        const appState = getAppState();
        const mapComponent = getMapComponent();

        appState.addDataset(dataset);
        appState.addLayerDef(layerDef);
        mapComponent.addLayer(layerDef);
        mapComponent.zoomToBounds(dataset.calcBounds());

        getAppComponent().refresh();
    }

    private addPlatts0_60() {
        const dataset = PLATTS_0_60();
        const layerDef = LayerUtil.createLayerDef("platts_0_60", "PLATTS (0 - 60%)", dataset);
        layerDef.config = {
            ...layerDef.config,
            sizeFieldId: "5801",
            colorFieldId: "5683",
            opacity: 80
        };
        this.addDatasetAndLayer(dataset, layerDef);
    }

    private addPlatts40_100() {
        const dataset = PLATTS_40_100();
        const layerDef = LayerUtil.createLayerDef("platts_40_100", "PLATTS (40 - 100%)", dataset);
        layerDef.config = {
            ...layerDef.config,
            colorPalette: [
                Color.createFromString("#66C2A5"),
                Color.createFromString("#FC8D62"),
                Color.createFromString("#8DA0CB"),
                Color.createFromString("#E78AC3"),
                Color.createFromString("#A6D854"),
                Color.createFromString("#FFD92F"),
                Color.createFromString("#E5C494"),
                Color.createFromString("#B3B3B3"),
            ],
            sizeFieldId: "5801",
            colorFieldId: "5683",
            opacity: 100,
            shape: "circle"
        };
        this.addDatasetAndLayer(dataset, layerDef);
    }

    private addVesselData() {
        const dataset = VESSELS();
        const layerDef = LayerUtil.createLayerDef("vessels", "VESSELS", dataset);
        layerDef.config = {
            ...layerDef.config,
            colorPalette: [
                Color.createFromString("#66C2A5"),
                Color.createFromString("#FC8D62"),
                Color.createFromString("#8DA0CB"),
                Color.createFromString("#E78AC3"),
                Color.createFromString("#A6D854"),
            ],
            sizeDependsOnSizeField: false,
            colorFieldId: "Capacity",
            opacity: 100,
            shape: "vessel",
            useHeadingField: true
        };
        layerDef.headingFieldId = "Heading";
        this.addDatasetAndLayer(dataset, layerDef);
    }

    private addPolygonData1() {
        const dataset = POLYGON_DATASET_1();
        const layerDef = LayerUtil.createLayerDef("polygons", "POLYGONS", dataset, "geojson");
        layerDef.config = {
            ...layerDef.config,
            colorPalette: [
                Color.createFromString("#66C2A5"),
                Color.createFromString("#FC8D62"),
                Color.createFromString("#8DA0CB"),
                Color.createFromString("#E78AC3"),
                Color.createFromString("#A6D854"),
            ],
            sizeDependsOnSizeField: false,
            colorFieldId: "5807",
            opacity: 100,
            useHeadingField: true
        };
        this.addDatasetAndLayer(dataset, layerDef);
    }

    private manageState() {
        const txtAreaId = "__dev_setgetstate_textarea";

        const closeDialog = () => getAppComponent().hideDialog();
        const setState = () => {
            const textArea: any = document.getElementById(txtAreaId);
            if (textArea) {
                getApi().setState(textArea.value);
            }
        };
        const getState = () => {
            const textArea: any = document.getElementById(txtAreaId);
            if (textArea) {
                textArea.value = JSON.stringify(getApi().getState(true), null, 4);
            }
        };
        const getAllSymbols = () => {
            const textArea: any = document.getElementById(txtAreaId);
            if (textArea) {
                let txt = "";
                for (const ds of getAppState().getDatasets()) {
                    txt += `${ds.getName()} (ID = ${ds.getId()}, ${ds.getSize()} rows)\n-------------------------------------------`;
                    for (const row of ds.getDataRows()) {
                        txt += `\n${row.getIdent()}`;
                    }
                }
                textArea.value = txt;
            }
        };

        const dialog = (
            <Dialog
                onClose={closeDialog}
                title={"SET / GET Application State"}
                closeControl={true}
                modal={true}
            >
                <div css={css`width: 46vw; overflow: hidden; margin: 1em`}>
                    <TextArea id={txtAreaId} style={{height: "40vh"}} />
                </div>
                <Container pad>
                    <Toolbar bordered={false}>
                        <Button onClick={setState}>Set State</Button>
                        <Button onClick={getState}>Get State</Button>
                        <Button onClick={getAllSymbols}>Get Symbols</Button>
                        <ToolbarSpacer />
                        <Button onClick={closeDialog}>Close</Button>
                    </Toolbar>
                </Container>
            </Dialog>
        );
        getAppComponent().showDialog(dialog);
    }

    private deleteAllData = () => {
        const appState = getAppState();
        const mapComponent = getMapComponent();

        for (const ds of appState.getDatasets()) {
            const layerDefsToDelete = appState.getLayerDefs(ds);
            appState.deleteDataset(ds);
            for (const def of layerDefsToDelete) {
                mapComponent.deleteLayer(def);
            }
        }

        mapComponent.resetState();
        getAppComponent().refresh();
    }

    private testPolygonService() {
        const txtAreaId = "__dev_polygonservice_textarea";

        const setText = (text: string) => {
            const textArea: any = document.getElementById(txtAreaId);
            if (textArea) {
                textArea.value = text;
            }
        }

        const closeDialog = () => getAppComponent().hideDialog();

        const download = () => {
            const populateGeoJson = (job) => {
                if (!job) {
                    setText("Downloading...");
                } else {
                    let geometry = PolygonProvider.getInstance().getGeometry(country, state, county);
                    if (geometry) {
                        setText(JSON.stringify(geometry, null, 4));
                    } else if (job.hasErrors()) {
                        setText("Error");
                    } else {
                        setText("No geometry!");
                    }
                }
            }
            PolygonProvider.getInstance().startJobIfNeeded([[country, state, county]], job => populateGeoJson(job))
            populateGeoJson(null);
        };

        const renderLabel = (label: string) => <span style={{padding: "0 0.4em"}}>{label}</span>

        let country: string = null;
        let state: string = null;
        let county: string = null;

        const dialog = (
            <Dialog
                onClose={closeDialog}
                title={"Test Polygon Service"}
                closeControl={true}
                modal={true}
            >
                <div>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", margin: "1rem"}}>
                        {renderLabel("Country:")}
                        <TextInput defaultValue={""} onChange={event => country = event.target.value} />
                        {renderLabel("State:")}
                        <TextInput defaultValue={""} onChange={event => state = event.target.value}/>
                        {renderLabel("County:")}
                        <TextInput defaultValue={""} onChange={event => county = event.target.value}/>
                    </div>
                    <div css={css`width: 46vw; overflow: hidden; margin: 1em`}>
                        <TextArea id={txtAreaId} style={{height: "40vh"}} />
                    </div>
                </div>
                <Container pad>
                    <Toolbar bordered={false}>
                        <Button onClick={download}>Download</Button>
                        <ToolbarSpacer />
                        <Button onClick={closeDialog}>Close</Button>
                    </Toolbar>
                </Container>
            </Dialog>
        );
        getAppComponent().showDialog(dialog);
    }

    render = () => {
        return (
            <div className={"devpanel"}>
                {
                    this.ACTIONS.map(ac => {
                        if (ac.action) {
                            return (
                                <div onClick={ac.action} css={(theme) => css`
                                  cursor: pointer;
                                  color: ${theme.focusAndAccentColor};
                                  &:hover {
                                    ${`color: ${theme.listItem.selected.highlightColor}`}
                                  }
                                `}>
                                    {ac.name}
                                </div>
                            );
                        } else {
                            return <h3>{ac.name}</h3>;
                        }
                    })
                }
            </div>
        );
    }

}
